import React, { useState } from 'react';
import { Box, IconButton, Button, Stack, Tooltip, Popover, Typography } from '@mui/material';
import { Send as SendIcon, ViewCompact as ViewCompactIcon, ViewModule as ViewModuleIcon, MoreVert as MoreVertIcon, HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import MarkdownEditor from '../../../../components/Common/MarkdownEditor';
import CodeComponent from '../../../../components/Exercise/Code';

const SYMBOL_USAGE_CONTENT = (
  <>
    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>符号使用说明</Typography>
    <Typography variant="body2" component="div" sx={{ '& p': { mb: 0.5 }, '& code': { bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 } }}>
      <p>• 数学公式用 <code>$...$</code> 行内、<code>$$...$$</code> 块级</p>
      <p>• 带上下界积分：<code>{'$\\int_0^1 x^2 \\, dx$'}</code></p>
      <p>• 求和：<code>{'$\\sum_{i=1}^{n} a_i$'}</code></p>
      <p>• 分数：<code>{'$\\frac{a}{b}$'}</code></p>
      <p>• 根号：<code>{'$\\sqrt{x}$'}</code> 或 <code>{'$\\sqrt[n]{x}$'}</code></p>
    </Typography>
  </>
);

export default function ChatInputPanel({
  inputMessage,
  setInputMessage,
  sending,
  chatWidthMode,
  setChatWidthMode,
  handleSend,
  lastExerciseAnswerInInput = false,
  lastExerciseCode = null,
  handleExerciseAnswerSubmitted,
  codeEditorOpen = true,
  setCodeEditorOpen,
}) {
  const [symbolPanelOpen, setSymbolPanelOpen] = useState(false);
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);

  return (
    <Box
      sx={{
        width: chatWidthMode === 'wide' ? 640 : 360,
        minWidth: chatWidthMode === 'wide' ? 640 : 360,
        flexShrink: 0,
        position: 'relative',
        zIndex: 1,
        m: 2,
        borderRadius: 2,
        border: '1px solid rgba(255, 255, 255, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        minHeight: 0,
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(6px) saturate(120%)',
        WebkitBackdropFilter: 'blur(6px) saturate(120%)',
        transition: 'width 0.25s ease, min-width 0.25s ease',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Tooltip title="符号使用说明" placement="top">
          <IconButton
            size="small"
            onClick={(e) => setHelpAnchorEl(e.currentTarget)}
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' },
            }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            onClick={() => setChatWidthMode('normal')}
            title="标准视图"
            sx={{
              color: chatWidthMode === 'normal' ? 'primary.main' : 'text.secondary',
              bgcolor: chatWidthMode === 'normal' ? 'rgba(102, 126, 234, 0.2)' : 'transparent',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' },
            }}
          >
            <ViewCompactIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setChatWidthMode('wide')}
            title="宽视图"
            sx={{
              color: chatWidthMode === 'wide' ? 'primary.main' : 'text.secondary',
              bgcolor: chatWidthMode === 'wide' ? 'rgba(102, 126, 234, 0.2)' : 'transparent',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' },
            }}
          >
            <ViewModuleIcon fontSize="small" />
          </IconButton>
          <Tooltip title="插入符号" placement="top">
            <IconButton
              size="small"
              onClick={() => setSymbolPanelOpen((v) => !v)}
              sx={{
                color: symbolPanelOpen ? 'primary.main' : 'text.secondary',
                bgcolor: symbolPanelOpen ? 'rgba(102, 126, 234, 0.2)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Popover
        open={Boolean(helpAnchorEl)}
        anchorEl={helpAnchorEl}
        onClose={() => setHelpAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, maxWidth: 320 } }}
      >
        {SYMBOL_USAGE_CONTENT}
      </Popover>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {lastExerciseCode && codeEditorOpen ? (
          <>
            <Box sx={{ flexShrink: 0, maxHeight: 720, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
              <CodeComponent
                example={lastExerciseCode.example}
                embedded
                splitLayout
                onAnswerSubmitted={handleExerciseAnswerSubmitted}
              />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', mt: 1, borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
              <MarkdownEditor
                value={inputMessage}
                onChange={setInputMessage}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                label=""
                minRows={4}
                maxRows={4}
                showPreview={false}
                toolbarMode="symbolOnly"
                symbolPanelOpen={symbolPanelOpen}
                onSymbolPanelOpenChange={setSymbolPanelOpen}
                placeholder="输入问题，继续聊天…"
                disabled={sending}
                disableImageUpload={true}
                fillHeight={true}
              />
              <Box sx={{ mt: 1.5, flexShrink: 0 }}>
                <Button
                  variant="contained"
                  onClick={() => handleSend()}
                  disabled={sending || !inputMessage.trim()}
                  startIcon={<SendIcon />}
                  fullWidth
                  sx={{ height: 40, '&.Mui-disabled': { bgcolor: 'grey.700', color: 'grey.500' } }}
                >
                  发送
                </Button>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <MarkdownEditor
              value={inputMessage}
              onChange={setInputMessage}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              label=""
              minRows={6}
              maxRows={12}
              showPreview={false}
              toolbarMode="symbolOnly"
              symbolPanelOpen={symbolPanelOpen}
              onSymbolPanelOpenChange={setSymbolPanelOpen}
              placeholder={lastExerciseAnswerInInput ? '在此输入你的答案并点击发送' : '输入你的问题，可点击「插入符号」插入数学符号...'}
              disabled={sending}
              disableImageUpload={true}
              fillHeight={true}
            />
            <Box sx={{ mt: 1.5, flexShrink: 0, px: 2 }}>
              <Button
                variant="contained"
                onClick={() => handleSend()}
                disabled={sending || !inputMessage.trim()}
                startIcon={<SendIcon />}
                fullWidth
                sx={{
                  height: 48,
                  '&.Mui-disabled': { bgcolor: 'grey.700', color: 'grey.500' },
                }}
              >
                {lastExerciseAnswerInInput ? '发送答案' : '发送'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
